import type { IManagementReportMonth } from '@commenda/api';

import { type BucketTotals, ReportingBucket } from '../types/buckets';
import type {
  IBalanceFacts,
  ICashFlowFacts,
  IFactTable,
  IMonthFacts,
  IPnlFacts,
  IReportedTotals,
  IWorkingCapitalFacts,
} from '../types/metrics';
import type { MonthKey } from '../types/period';
import type { IManagementReportSource } from '../types/report';
import { BucketHelper, OPEX_BUCKETS } from './BucketHelper';
import { LedgerHelper } from './LedgerHelper';
import { MonthKeyHelper } from './MonthKeyHelper';

/** Builds the per-month fact table every report component reads from. */
export class FactTableHelper {
  /**
   * One row per month holding every derived figure. Closed months go in
   * `byMonth`; months past `booksClosedThrough` go in `preliminaryByMonth`
   * only, so nothing that aggregates can pick them up by accident.
   * A dataset whose books were never closed yields an empty closed table.
   */
  static build(source: IManagementReportSource): IFactTable {
    const { dataset, currency, bucketOverrides } = source;
    const sorted = [...dataset.months].sort((a, b) =>
      a.month.localeCompare(b.month)
    );
    const booksClosedThrough = dataset.booksClosedThrough ?? '';
    const closedMonths = sorted.filter((m) => m.month <= booksClosedThrough);
    const openMonths = sorted.filter((m) => m.month > booksClosedThrough);
    const lines = LedgerHelper.collectLines(sorted);
    const bucketMap = BucketHelper.buildMap(lines, bucketOverrides);

    const byMonth = FactTableHelper.monthFactsMap(closedMonths, bucketMap);
    const lastClosed = closedMonths[closedMonths.length - 1];
    const preliminaryByMonth = FactTableHelper.monthFactsMap(
      openMonths,
      bucketMap,
      lastClosed ? (byMonth.get(lastClosed.month)?.balance ?? null) : null
    );

    const latest = sorted[sorted.length - 1];
    return {
      companyName: dataset.company.name,
      currency,
      booksClosedThrough,
      fiscalYearStartMonth: dataset.company.fiscalYearStartMonth,
      months: closedMonths.map((m) => m.month),
      byMonth,
      preliminaryMonths: openMonths.map((m) => m.month),
      preliminaryByMonth,
      lines,
      lineById: new Map(lines.map((l) => [l.id, l])),
      bucketMap,
      latestDataMonth: latest ? latest.month : null,
    };
  }

  static pnlFromBuckets(b: BucketTotals): IPnlFacts {
    const revenue = b.REVENUE;
    const costOfGoodsSold = b.COST_OF_GOODS_SOLD;
    const grossProfit = revenue - costOfGoodsSold;
    const opexByBucket: Partial<Record<ReportingBucket, number>> =
      Object.fromEntries(OPEX_BUCKETS.map((k) => [k, b[k]]));
    const opex = BucketHelper.sum(b, OPEX_BUCKETS);
    const depreciationAmortisation = b.DEPRECIATION_AMORTISATION;
    const operatingProfit = grossProfit - opex;
    const ebitda = operatingProfit + depreciationAmortisation;
    const netProfit =
      operatingProfit +
      b.NON_OPERATING_INCOME -
      b.NON_OPERATING_EXPENSE -
      b.INCOME_TAX;
    return {
      revenue,
      costOfGoodsSold,
      grossProfit,
      grossMargin: FactTableHelper.ratio(grossProfit, revenue),
      opex,
      opexByBucket,
      payroll: b.OPEX_PAYROLL,
      nonPayrollOpex: opex - b.OPEX_PAYROLL,
      depreciationAmortisation,
      ebitda,
      ebitdaMargin: FactTableHelper.ratio(ebitda, revenue),
      operatingProfit,
      nonOperatingIncome: b.NON_OPERATING_INCOME,
      nonOperatingExpense: b.NON_OPERATING_EXPENSE,
      incomeTax: b.INCOME_TAX,
      netProfit,
      netMargin: FactTableHelper.ratio(netProfit, revenue),
    };
  }

  static balanceFromBuckets(b: BucketTotals): IBalanceFacts {
    const totalAssets = BucketHelper.sum(b, [
      ReportingBucket.CASH,
      ReportingBucket.INVESTMENTS,
      ReportingBucket.ACCOUNTS_RECEIVABLE,
      ReportingBucket.INVENTORY,
      ReportingBucket.OTHER_CURRENT_ASSETS,
      ReportingBucket.FIXED_ASSETS,
      ReportingBucket.OTHER_NON_CURRENT_ASSETS,
    ]);
    const totalLiabilities = BucketHelper.sum(b, [
      ReportingBucket.ACCOUNTS_PAYABLE,
      ReportingBucket.DEBT,
      ReportingBucket.OTHER_CURRENT_LIABILITIES,
      ReportingBucket.OTHER_NON_CURRENT_LIABILITIES,
    ]);
    const totalEquity = BucketHelper.sum(b, [
      ReportingBucket.SHARE_CAPITAL,
      ReportingBucket.RETAINED_EARNINGS,
      ReportingBucket.OTHER_EQUITY,
    ]);
    return {
      cash: b.CASH,
      investments: b.INVESTMENTS,
      cashAndInvestments: b.CASH + b.INVESTMENTS,
      accountsReceivable: b.ACCOUNTS_RECEIVABLE,
      inventory: b.INVENTORY,
      otherCurrentAssets: b.OTHER_CURRENT_ASSETS,
      fixedAssets: b.FIXED_ASSETS,
      otherNonCurrentAssets: b.OTHER_NON_CURRENT_ASSETS,
      totalAssets,
      accountsPayable: b.ACCOUNTS_PAYABLE,
      debt: b.DEBT,
      otherCurrentLiabilities: b.OTHER_CURRENT_LIABILITIES,
      otherNonCurrentLiabilities: b.OTHER_NON_CURRENT_LIABILITIES,
      totalLiabilities,
      shareCapital: b.SHARE_CAPITAL,
      retainedEarnings: b.RETAINED_EARNINGS,
      otherEquity: b.OTHER_EQUITY,
      totalEquity,
    };
  }

  /**
   * Indirect-method cash flow from balance sheet deltas. Without a prior month
   * the flows are zero and only the balances are meaningful.
   */
  static deriveCashFlow(
    pnl: IPnlFacts,
    balance: IBalanceFacts,
    prior: IBalanceFacts | null,
    reported: IReportedTotals
  ): ICashFlowFacts {
    const hasOpening = prior !== null;
    const p = prior ?? balance;
    const netIncome = pnl.netProfit;
    const da = pnl.depreciationAmortisation;
    const changeInReceivables =
      balance.accountsReceivable - p.accountsReceivable;
    const changeInInventory = balance.inventory - p.inventory;
    const changeInOtherCurrentAssets =
      balance.otherCurrentAssets - p.otherCurrentAssets;
    const changeInPayables = balance.accountsPayable - p.accountsPayable;
    const changeInOtherLiabilities =
      balance.otherCurrentLiabilities -
      p.otherCurrentLiabilities +
      (balance.otherNonCurrentLiabilities - p.otherNonCurrentLiabilities);
    const workingCapitalChange =
      -changeInReceivables -
      changeInInventory -
      changeInOtherCurrentAssets +
      changeInPayables +
      changeInOtherLiabilities;
    const operating = hasOpening
      ? netIncome + da + workingCapitalChange
      : (reported.cfsOperating ?? 0);

    const capex = balance.fixedAssets - p.fixedAssets + da;
    const investmentsPurchased = balance.investments - p.investments;
    const otherInvesting =
      balance.otherNonCurrentAssets - p.otherNonCurrentAssets;
    const investing = hasOpening
      ? -(capex + investmentsPurchased + otherInvesting)
      : (reported.cfsInvesting ?? 0);

    const debtRaised = balance.debt - p.debt;
    const equityRaised = balance.shareCapital - p.shareCapital;
    const otherFinancing =
      balance.otherEquity -
      p.otherEquity +
      (balance.retainedEarnings - p.retainedEarnings - netIncome);
    const financing = hasOpening
      ? debtRaised + equityRaised + otherFinancing
      : (reported.cfsFinancing ?? 0);

    const openingCash = hasOpening
      ? p.cash
      : (reported.cfsOpeningCash ?? balance.cash);
    const closingCash = balance.cash;
    const actualChange =
      hasOpening || reported.cfsOpeningCash !== null
        ? closingCash - openingCash
        : 0;
    const derived = operating + investing + financing;

    const cfsReported =
      reported.cfsOperating !== null &&
      reported.cfsInvesting !== null &&
      reported.cfsFinancing !== null
        ? reported.cfsOperating + reported.cfsInvesting + reported.cfsFinancing
        : null;

    return {
      hasOpening,
      openingCash,
      netIncome,
      depreciationAmortisation: da,
      changeInReceivables,
      changeInInventory,
      changeInOtherCurrentAssets,
      changeInPayables,
      changeInOtherLiabilities,
      workingCapitalChange,
      operating,
      capex,
      investmentsPurchased,
      otherInvesting,
      investing,
      debtRaised,
      equityRaised,
      otherFinancing,
      financing,
      unexplained: hasOpening ? actualChange - derived : 0,
      netChange: actualChange,
      closingCash,
      netBurn: -(operating + investing + investmentsPurchased),
      cfsVariance:
        hasOpening && cfsReported !== null ? derived - cfsReported : null,
    };
  }

  static workingCapitalDays(
    pnl: IPnlFacts,
    balance: IBalanceFacts,
    days: number
  ): IWorkingCapitalFacts {
    const dso =
      pnl.revenue > 0
        ? (balance.accountsReceivable / pnl.revenue) * days
        : null;
    const dpo =
      pnl.costOfGoodsSold > 0
        ? (balance.accountsPayable / pnl.costOfGoodsSold) * days
        : null;
    const dio =
      pnl.costOfGoodsSold > 0
        ? (balance.inventory / pnl.costOfGoodsSold) * days
        : null;
    const cashConversionCycle =
      dso !== null && dpo !== null ? dso + (dio ?? 0) - dpo : null;
    return { dso, dpo, dio, cashConversionCycle };
  }

  /** Chains each month's cash flow off the previous month's balances, starting from `openingBalance`. */
  private static monthFactsMap(
    months: IManagementReportMonth[],
    bucketMap: Map<string, ReportingBucket>,
    openingBalance: IBalanceFacts | null = null
  ): Map<MonthKey, IMonthFacts> {
    return months.reduce<Map<MonthKey, IMonthFacts>>((acc, month, i) => {
      const priorMonth = months[i - 1];
      const prior = priorMonth
        ? (acc.get(priorMonth.month)?.balance ?? null)
        : openingBalance;
      return acc.set(
        month.month,
        FactTableHelper.monthFacts(month, bucketMap, prior)
      );
    }, new Map());
  }

  private static monthFacts(
    month: IManagementReportMonth,
    bucketMap: Map<string, ReportingBucket>,
    prior: IBalanceFacts | null
  ): IMonthFacts {
    const buckets = BucketHelper.emptyTotals();
    const lines: Record<string, number> = {};
    LedgerHelper.flattenMonth(month).forEach(({ line, value }) => {
      lines[line.id] = (lines[line.id] ?? 0) + value;
      buckets[bucketMap.get(line.id) ?? ReportingBucket.EXCLUDED] += value;
    });
    const reported = FactTableHelper.reportedTotals(month);
    const pnl = FactTableHelper.pnlFromBuckets(buckets);
    const balance = FactTableHelper.balanceFromBuckets(buckets);
    const days = MonthKeyHelper.daysIn(month.month);
    return {
      month: month.month,
      days,
      buckets,
      lines,
      reported,
      pnl,
      balance,
      cashFlow: FactTableHelper.deriveCashFlow(pnl, balance, prior, reported),
      workingCapital: FactTableHelper.workingCapitalDays(pnl, balance, days),
    };
  }

  private static reportedTotals(m: IManagementReportMonth): IReportedTotals {
    const is = m.incomeStatement;
    const bs = m.balanceSheet;
    const cfs = m.cashFlowStatement;
    return {
      revenue: LedgerHelper.sectionTotal(is.revenue),
      costOfGoodsSold: LedgerHelper.sectionTotal(is.cost_of_goods_sold),
      grossProfit: is.gross_profit,
      operatingExpenses: LedgerHelper.sectionTotal(is.operating_expenses),
      operatingProfit: is.operating_profit,
      nonOperatingRevenue: LedgerHelper.sectionTotal(is.non_operating_revenue),
      nonOperatingExpenses: LedgerHelper.sectionTotal(
        is.non_operating_expenses
      ),
      netProfit: is.net_profit,
      totalAssets: LedgerHelper.sectionTotal(bs.assets),
      totalLiabilities: LedgerHelper.sectionTotal(bs.liabilities),
      totalEquity: LedgerHelper.sectionTotal(bs.equity),
      cfsOperating: cfs
        ? LedgerHelper.sectionTotal(cfs.operating_activities)
        : null,
      cfsInvesting: cfs
        ? LedgerHelper.sectionTotal(cfs.investing_activities)
        : null,
      cfsFinancing: cfs
        ? LedgerHelper.sectionTotal(cfs.financing_activities)
        : null,
      cfsOpeningCash: cfs?.cash_at_beginning_of_period ?? null,
      cfsClosingCash: cfs?.cash_at_end_of_period ?? null,
    };
  }

  private static ratio(numerator: number, denominator: number): number | null {
    return denominator ? numerator / denominator : null;
  }
}
